import { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Receipt, User, CreditCard, Banknote, Coins, Download, Loader2, PackageCheck, Globe, CheckCircle, Pencil, Wrench, Package, Plus, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../App";

const statusConfig = {
  en_attente: { label: "En attente", color: "bg-yellow-100 text-yellow-800" },
  payee: { label: "Payée", color: "bg-green-100 text-green-800" },
  a_retirer: { label: "À retirer", color: "bg-blue-100 text-blue-800" },
  remise: { label: "Remise", color: "bg-emerald-100 text-emerald-800" },
  annulee: { label: "Annulée", color: "bg-red-100 text-red-800" },
};

const paymentLabels = {
  especes: "Espèces",
  cb: "CB",
  stripe: "Stripe",
  cheque: "Chèque",
  virement: "Virement",
  credit: "Crédit boutique",
  "credit+especes": "Crédit + Espèces",
  "credit+cb": "Crédit + CB",
  "credit+stripe": "Crédit + Stripe",
};

const typeVenteConfig = {
  produit: { label: "Produit", icon: Package, color: "bg-indigo-100 text-indigo-700" },
  service: { label: "Service", icon: Wrench, color: "bg-amber-100 text-amber-700" },
  mixte: { label: "Mixte", icon: ShoppingCart, color: "bg-violet-100 text-violet-700" },
};

const SalesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [addLinesSaleId, setAddLinesSaleId] = useState(null);
  const [newLines, setNewLines] = useState([{ description: "", quantite: 1, prix_unitaire: 0 }]);
  const [addingLines, setAddingLines] = useState(false);

  const downloadPdf = async (url, filename) => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }
    setDownloadingPdf(url);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erreur téléchargement");
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Erreur lors du téléchargement du PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      const response = await API.get("/sales", { params: { limit: 100 } });
      setSales(response.data.sales);
      setTotal(response.data.total);
    } catch {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSales(); }, []);

  const updateSaleStatus = async (saleId, newStatus) => {
    setUpdatingStatus(saleId);
    try {
      await API.put(`/sales/${saleId}/status`, { statut: newStatus });
      toast.success(newStatus === "remise" ? "Produit remis au client" : "Statut mis à jour");
      fetchSales();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const updatePaymentMethod = async (saleId, newMethod) => {
    try {
      await API.put(`/sales/${saleId}`, { mode_paiement: newMethod });
      toast.success("Mode de paiement modifié");
      setEditingSaleId(null);
      fetchSales();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg).join(", ") : (typeof detail === "string" ? detail : "Erreur"));
    }
  };

  const addLinesToSale = async () => {
    const validLines = newLines.filter(l => l.description.trim() && l.prix_unitaire > 0);
    if (validLines.length === 0) {
      toast.error("Ajoutez au moins une ligne avec une désignation et un prix");
      return;
    }
    setAddingLines(true);
    try {
      await API.post(`/sales/${addLinesSaleId}/lignes`, { lignes: validLines });
      toast.success("Lignes ajoutées à la facture");
      setAddLinesSaleId(null);
      setNewLines([{ description: "", quantite: 1, prix_unitaire: 0 }]);
      fetchSales();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    } finally {
      setAddingLines(false);
    }
  };

  const filteredSales = sales.filter(s => {
    if (filterStatus !== "all" && s.statut !== filterStatus) return false;
    if (filterType !== "all" && s.type_vente_detail !== filterType) return false;
    return true;
  });
  const onlineOrders = sales.filter(s => s.type_vente === "en_ligne");
  const pendingPickup = sales.filter(s => s.statut === "a_retirer");
  const totalRevenue = sales.reduce((sum, s) => sum + (s.total_ttc || 0), 0);
  const totalCommissions = sales.reduce((sum, s) => sum + (s.commission?.montant || 0), 0);
  const nbProduits = sales.filter(s => s.type_vente_detail === "produit").length;
  const nbServices = sales.filter(s => s.type_vente_detail === "service").length;
  const nbMixtes = sales.filter(s => s.type_vente_detail === "mixte").length;

  const paymentIcons = { especes: Banknote, cb: CreditCard, stripe: Receipt, cheque: Receipt };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Historique des ventes</h1>
        <p className="text-muted-foreground">{total} ventes enregistrées</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <ShoppingCart className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total ventes</p>
                <p className="text-xl font-bold">{total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100">
                <Receipt className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Chiffre d'affaires</p>
                <p className="text-xl font-bold">{totalRevenue.toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-100">
                <Coins className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Commissions</p>
                <p className="text-xl font-bold">{totalCommissions.toFixed(2)}€</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                <Globe className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Commandes en ligne</p>
                <p className="text-xl font-bold">{onlineOrders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={pendingPickup.length > 0 ? "border-blue-300 bg-blue-50/50" : ""}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
                <PackageCheck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">À retirer</p>
                <p className="text-xl font-bold">{pendingPickup.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
          {[
            { key: "all", label: "Toutes" },
            { key: "a_retirer", label: "À retirer" },
            { key: "payee", label: "Payées" },
            { key: "remise", label: "Remises" },
            { key: "en_attente", label: "En attente" },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filterStatus === tab.key ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label}
              {tab.key !== "all" && ` (${sales.filter(s => s.statut === tab.key).length})`}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
          {[
            { key: "all", label: "Tout type" },
            { key: "produit", label: "Produits", count: nbProduits },
            { key: "service", label: "Services", count: nbServices },
            { key: "mixte", label: "Mixtes", count: nbMixtes },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilterType(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filterType === tab.key ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`filter-type-${tab.key}`}
            >
              {tab.label}
              {tab.key !== "all" && ` (${tab.count})`}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="font-medium">Aucune vente trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Articles</TableHead>
                    <TableHead>Paiement</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map((sale) => {
                    const PaymentIcon = paymentIcons[sale.mode_paiement] || Receipt;
                    const status = statusConfig[sale.statut] || statusConfig.en_attente;
                    const isOnline = sale.type_vente === "en_ligne";
                    return (
                      <TableRow key={sale.id} data-testid={`sale-row-${sale.id}`} className={sale.statut === "a_retirer" ? "bg-blue-50/50" : ""}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium">{sale.numero}</span>
                            {isOnline && <Globe className="h-3.5 w-3.5 text-blue-500" title="Commande en ligne" />}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(sale.created_at).toLocaleDateString("fr-FR", {
                            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
                          })}
                        </TableCell>
                        <TableCell>{sale.client_nom || <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell>
                          {(() => {
                            const typeConf = typeVenteConfig[sale.type_vente_detail] || typeVenteConfig.produit;
                            const TypeIcon = typeConf.icon;
                            return (
                              <Badge className={typeConf.color + " gap-1"}>
                                <TypeIcon className="h-3 w-3" />
                                {typeConf.label}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge className={status.color} data-testid={`status-${sale.id}`}>{status.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {sale.articles?.slice(0, 2).map((article, i) => (
                              <p key={i} className="text-sm">{article.quantite}x {article.nom_produit}</p>
                            ))}
                            {sale.articles?.length > 2 && (
                              <p className="text-xs text-muted-foreground">+{sale.articles.length - 2} autre(s)</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {editingSaleId === sale.id ? (
                            <div className="flex items-center gap-1">
                              <select
                                defaultValue={sale.mode_paiement}
                                onChange={(e) => updatePaymentMethod(sale.id, e.target.value)}
                                className="h-8 rounded-md border border-input bg-background px-2 py-1 text-sm focus:ring-2 focus:ring-ring"
                                data-testid={`edit-payment-${sale.id}`}
                                autoFocus
                                onBlur={() => setEditingSaleId(null)}
                              >
                                <option value="especes">Espèces</option>
                                <option value="cb">CB</option>
                                <option value="cheque">Chèque</option>
                                <option value="stripe">Stripe</option>
                                <option value="virement">Virement</option>
                              </select>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group">
                              <PaymentIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="capitalize text-sm">{paymentLabels[sale.mode_paiement] || sale.mode_paiement}</span>
                              <button
                                onClick={() => setEditingSaleId(sale.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted"
                                title="Modifier le mode de paiement"
                                data-testid={`btn-edit-payment-${sale.id}`}
                              >
                                <Pencil className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">{sale.total_ttc?.toFixed(2)}€</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {sale.statut === "a_retirer" && (
                              <Button
                                size="sm"
                                onClick={() => updateSaleStatus(sale.id, "remise")}
                                disabled={updatingStatus === sale.id}
                                data-testid={`btn-remise-${sale.id}`}
                              >
                                {updatingStatus === sale.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
                                Remis
                              </Button>
                            )}
                            {sale.statut === "remise" && sale.date_remise && (
                              <span className="text-xs text-muted-foreground">
                                {new Date(sale.date_remise).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            )}
                            <button
                              onClick={() => downloadPdf(`/api/pdf/facture/${sale.id}`, `Facture_${sale.numero || sale.id}.pdf`)}
                              disabled={downloadingPdf === `/api/pdf/facture/${sale.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                              data-testid={`pdf-facture-${sale.id}`}
                            >
                              {downloadingPdf === `/api/pdf/facture/${sale.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                            </button>
                            {user?.role === "admin" && (
                              <button
                                onClick={() => { setAddLinesSaleId(sale.id); setNewLines([{ description: "", quantite: 1, prix_unitaire: 0 }]); }}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-primary text-primary bg-background hover:bg-primary/10"
                                title="Ajouter des lignes"
                                data-testid={`add-lines-${sale.id}`}
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Add Lines Modal */}
      {addLinesSaleId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setAddLinesSaleId(null)}>
          <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-lines-modal">
            <h3 className="text-lg font-semibold mb-1">Ajouter des lignes à la facture</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {sales.find(s => s.id === addLinesSaleId)?.numero} — Total actuel : {(sales.find(s => s.id === addLinesSaleId)?.total_ttc || 0).toFixed(2)} EUR
            </p>
            <div className="space-y-3">
              {newLines.map((line, idx) => (
                <div key={idx} className="flex gap-2 items-end">
                  <div className="flex-1">
                    {idx === 0 && <Label className="text-xs">Désignation</Label>}
                    <Input
                      value={line.description}
                      onChange={(e) => {
                        const updated = [...newLines];
                        updated[idx].description = e.target.value;
                        setNewLines(updated);
                      }}
                      placeholder="Ex: Marge atelier, Frais de gestion..."
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="w-16">
                    {idx === 0 && <Label className="text-xs">Qté</Label>}
                    <Input
                      type="number" min="1"
                      value={line.quantite}
                      onChange={(e) => {
                        const updated = [...newLines];
                        updated[idx].quantite = parseInt(e.target.value) || 1;
                        setNewLines(updated);
                      }}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="w-24">
                    {idx === 0 && <Label className="text-xs">Prix EUR</Label>}
                    <Input
                      type="number" step="0.01" min="0"
                      value={line.prix_unitaire}
                      onChange={(e) => {
                        const updated = [...newLines];
                        updated[idx].prix_unitaire = parseFloat(e.target.value) || 0;
                        setNewLines(updated);
                      }}
                      className="h-9 text-sm"
                    />
                  </div>
                  {newLines.length > 1 && (
                    <button onClick={() => setNewLines(newLines.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 p-1">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setNewLines([...newLines, { description: "", quantite: 1, prix_unitaire: 0 }])}
                className="text-xs text-primary hover:underline font-medium"
              >
                + Ajouter une ligne
              </button>
              {newLines.some(l => l.prix_unitaire > 0) && (
                <p className="text-sm font-medium text-right">
                  Nouveau total : {((sales.find(s => s.id === addLinesSaleId)?.total_ttc || 0) + newLines.reduce((sum, l) => sum + (l.quantite || 1) * (l.prix_unitaire || 0), 0)).toFixed(2)} EUR
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setAddLinesSaleId(null)}>Annuler</Button>
                <Button className="flex-1" onClick={addLinesToSale} disabled={addingLines}>
                  {addingLines ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Ajouter
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPage;
